/**
 * Hire checklist (2026-10-09, item 8, flag `hire_checklist`, default off). ApplicantStack's
 * "New Hires" list, the Arevina way: the employer sets up a checklist template once and applies it
 * to each hire; the hire gets a notice and an email with a link to their checklist page, where they
 * acknowledge policies and upload what is asked; the employer sees progress per hire (7/10).
 *
 * Item kinds:
 *   upload       a document. Fulfilled through the existing Request documents flow: applying the
 *                checklist creates ONE document request for all upload items (when the
 *                document_requests flag is on for the org); the item is done when that request item
 *                is complete. With document_requests off, the employer marks it done.
 *   acknowledge  read and acknowledge: a policy PDF (uploaded by the employer) or a link; the hire
 *                taps "I have read this" and the date is stored.
 *   orientation  attend orientation: done when the hire's attendance at one of the org's orientation
 *                sessions is marked attended (schedulingBookings, kind onboarding).
 *   task         a custom task the employer marks done.
 *
 * HARD RULE: no tax forms, no I-9, no Social Security numbers, no bank or direct deposit details,
 * no e-signatures on legal documents. blockedLabelReason() enforces it in the template editor AND
 * on the server (normalizeChecklistTemplate). Payroll and tax paperwork stays in the employer's HR
 * or payroll system.
 *
 * Data (server-only, firestore.rules default deny; read and written only by hireChecklistApi):
 *   organizations/{orgId}/hireChecklistTemplates/{templateId}   { name, items[], updatedAt, updatedBy }
 *   hireChecklists/{orgId}__{emtUid}                             one checklist per hire per org
 *
 * ONE new-hire checklist (2026-10-16, founder decision): this is now the only checklist for new hires.
 * The two older ones (credentialing document templates, organizations/{orgId}/credentialingTemplates,
 * and onboarding steps, organizations/{orgId}.onboardingTemplate) are no longer started for new hires;
 * hires who already have one keep it until it is done. What they offered lives here:
 *   - roleMatch: a template can be for positions containing a word ("Paramedic"); blank = everyone.
 *     When someone accepts a team invitation, the matching template is applied by itself
 *     (pickTemplateForHire; functions/src/hireChecklist.ts applyHireChecklistOnJoin).
 *   - dueDays: an item can be due N days after the checklist starts (stored as dueAtMs).
 *   - legacyChecklistsToTemplates: converts the older templates, for the employer's one-click
 *     "Add to my new hire checklist" (their own action; nothing is converted behind their back).
 *
 * Pure (no Firebase imports): copied into functions/src/generated/ by scripts/syncFunctionsGenerated.mjs.
 */
import { catalogOption, MAX_REQUEST_ITEMS } from '../documents/documentRequests';
import { mayAutoSendNow } from '../notifications/quietHours';
import { MS_PER_DAY, zonedDateKey } from '../scheduling/time';

export const HIRE_CHECKLIST_FLAG = 'hire_checklist';
export const HIRE_CHECKLIST_FLAG_LABEL = 'Hire checklist (new hire tasks and progress)';
export const HIRE_CHECKLIST_FLAG_DESCRIPTION =
  'Employers set up a checklist template once (documents to upload, policies to read and acknowledge, attend orientation, custom tasks) and apply it to each hire. The hire gets a notice, an email and reminders and completes their items from a checklist page; the employer sees progress per hire. No tax, I-9, Social Security, bank or e-signature items are allowed.';

/** Where the hire sees and completes their checklist (in-app notice + email link). */
export const HIRE_CHECKLIST_PATH = '/hire-checklist';

export const PAYROLL_NOTE =
  'Payroll and tax paperwork (W-4, I-9, Social Security numbers, bank and direct deposit details) and e-signatures on legal documents stay in your HR or payroll system. Arevina does not collect them.';

export const MAX_TEMPLATES = 20;
export const MAX_TEMPLATE_ITEMS = 30;
export const MAX_LABEL = 120;
export const MAX_DETAILS = 500;
export const MAX_TEMPLATE_NAME = 80;
export const MAX_ROLE_MATCH = 60;
export const MAX_DUE_DAYS = 365;
/** Policy PDFs (base64 through the callable): small enough for one call. */
export const MAX_POLICY_PDF_BYTES = 4 * 1024 * 1024;
/** Reminder emails per checklist, ever. */
export const MAX_REMINDERS = 4;
/** First reminder this long after the checklist was applied, then at most every REMINDER_GAP_MS. */
export const FIRST_REMINDER_AFTER_MS = 2 * MS_PER_DAY;
export const REMINDER_GAP_MS = 3 * MS_PER_DAY;

export type ChecklistItemKind = 'upload' | 'acknowledge' | 'orientation' | 'task';

export const ITEM_KIND_LABELS: Record<ChecklistItemKind, string> = {
  upload: 'Upload a document',
  acknowledge: 'Read and acknowledge',
  orientation: 'Attend orientation',
  task: 'Task (you mark it done)',
};

/** An upload item's document: one of the Request documents catalog keys, or a custom label. */
export type UploadDoc = { kind: 'catalog'; key: string } | { kind: 'custom' };

export type PolicyFile = { path: string; fileName: string; size: number };

export type TemplateItem = {
  id: string;
  kind: ChecklistItemKind;
  label: string;
  details: string;
  /** acknowledge: an https link to the policy (when there is no PDF). */
  url?: string | null;
  /** acknowledge: a PDF the employer uploaded (server-set only, never taken from a client). */
  policyFile?: PolicyFile | null;
  /** upload: what to request. */
  doc?: UploadDoc | null;
  /** Due this many days after the checklist starts (0 to 365); null = no due date. */
  dueDays?: number | null;
};

/** roleMatch: used by itself for positions containing this text (any case); blank = everyone. */
export type ChecklistTemplate = { id: string; name: string; items: TemplateItem[]; roleMatch?: string };

/* ------------------------------------------------------------------ the hard rule */

const BLOCKED: Array<{ re: RegExp; what: string }> = [
  { re: /\bw\s*-?\s*4\b/i, what: 'W-4' },
  { re: /\bw\s*-?\s*2\b/i, what: 'W-2' },
  { re: /\b1099\b/i, what: '1099' },
  { re: /\bi\s*-?\s*9\b/i, what: 'I-9' },
  { re: /\bssn\b/i, what: 'Social Security number' },
  { re: /social\s*-?\s*security/i, what: 'Social Security' },
  { re: /direct\s*-?\s*deposit/i, what: 'direct deposit' },
  { re: /bank\s*(account|acct|info|information|details|routing|statement)/i, what: 'bank account' },
  { re: /routing\s*(number|no\b|#|num)/i, what: 'routing number' },
  { re: /\btax\s*(form|forms|withholding|document|documents|paperwork)\b/i, what: 'tax forms' },
  { re: /\be\s*-?\s*sign/i, what: 'e-signature' },
  { re: /signature/i, what: 'signature' },
];

/**
 * Why a label or description may not be on a hire checklist, or null when it is fine. Payroll,
 * tax, identity-number and banking paperwork and e-signatures on legal documents never go through
 * Arevina.
 */
export function blockedLabelReason(text: unknown): string | null {
  if (typeof text !== 'string' || !text.trim()) return null;
  const hit = BLOCKED.find((b) => b.re.test(text));
  if (!hit) return null;
  return `"${hit.what}" can't be on a hire checklist. Payroll and tax paperwork (W-4, I-9, Social Security numbers, bank and direct deposit details) and e-signatures on legal documents stay in your HR or payroll system.`;
}

/* ------------------------------------------------------------------ template validation */

function clean(v: unknown, max: number): string {
  if (typeof v !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  return v.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max);
}

function cleanMultiline(v: unknown, max: number): string {
  if (typeof v !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  return v.replace(/[\u0000-\u0009\u000b-\u001f\u007f]/g, ' ').replace(/[ \t]+/g, ' ').trim().slice(0, max);
}

export function isHttpsUrl(v: unknown): v is string {
  if (typeof v !== 'string' || v.length > 1000) return false;
  try {
    const u = new URL(v.trim());
    return u.protocol === 'https:' && !!u.hostname;
  } catch {
    return false;
  }
}

const KINDS: ChecklistItemKind[] = ['upload', 'acknowledge', 'orientation', 'task'];
const ITEM_ID = /^[a-z0-9]{1,12}$/;

/**
 * Validates a template from the editor. `stored` is the template as saved before (the server passes
 * it), so a policy PDF attached earlier stays on its item: a client can never set a file path.
 */
export function normalizeChecklistTemplate(
  raw: unknown,
  stored?: ChecklistTemplate | null,
): { value: ChecklistTemplate | null; error: string | null } {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const name = clean(r.name, MAX_TEMPLATE_NAME);
  if (name.length < 2) return { value: null, error: 'Give the checklist a name (at least 2 characters).' };
  const nameBlocked = blockedLabelReason(name);
  if (nameBlocked) return { value: null, error: nameBlocked };
  const roleMatch = clean(r.roleMatch, MAX_ROLE_MATCH);
  const roleBlocked = blockedLabelReason(roleMatch);
  if (roleBlocked) return { value: null, error: roleBlocked };
  const rawItems = Array.isArray(r.items) ? r.items : [];
  if (!rawItems.length) return { value: null, error: 'Add at least one item.' };
  if (rawItems.length > MAX_TEMPLATE_ITEMS) return { value: null, error: `A checklist can have up to ${MAX_TEMPLATE_ITEMS} items.` };
  const storedItems = new Map((stored?.items || []).map((i) => [i.id, i]));
  const rawIds = new Set(rawItems.map((x) => (x as { id?: unknown })?.id).filter((x): x is string => typeof x === 'string'));
  const used = new Set<string>();
  const items: TemplateItem[] = [];
  let uploads = 0;
  let orientation = 0;
  for (let n = 0; n < rawItems.length; n++) {
    const it = (rawItems[n] && typeof rawItems[n] === 'object' ? rawItems[n] : {}) as Record<string, unknown>;
    const kind = it.kind as ChecklistItemKind;
    if (!KINDS.includes(kind)) return { value: null, error: `Item ${n + 1}: pick what kind of item it is.` };
    let label = clean(it.label, MAX_LABEL);
    const details = cleanMultiline(it.details, MAX_DETAILS);
    let doc: UploadDoc | null = null;
    if (kind === 'upload') {
      const d = (it.doc && typeof it.doc === 'object' ? it.doc : {}) as Record<string, unknown>;
      if (d.kind === 'catalog') {
        const opt = catalogOption(d.key);
        if (!opt) return { value: null, error: `Item ${n + 1}: that document is not on the list.` };
        doc = { kind: 'catalog', key: opt.key };
        if (!label) label = opt.label;
      } else {
        doc = { kind: 'custom' };
      }
      uploads++;
    }
    if (kind === 'orientation') {
      orientation++;
      if (!label) label = 'Attend orientation';
    }
    if (label.length < 2) return { value: null, error: `Item ${n + 1}: add a short name (at least 2 characters).` };
    const blocked = blockedLabelReason(label) || blockedLabelReason(details);
    if (blocked) return { value: null, error: `Item ${n + 1}: ${blocked}` };
    let url: string | null = null;
    if (kind === 'acknowledge' && typeof it.url === 'string' && it.url.trim()) {
      if (!isHttpsUrl(it.url)) return { value: null, error: `Item ${n + 1}: the policy link must start with https://` };
      url = it.url.trim();
    }
    let id = typeof it.id === 'string' && ITEM_ID.test(it.id) ? it.id : '';
    if (!id || used.has(id)) {
      // A new item never takes an id another item uses now or a stored item had (it would inherit its PDF).
      let k = n + 1;
      while (used.has(`i${k}`) || rawIds.has(`i${k}`) || storedItems.has(`i${k}`)) k++;
      id = `i${k}`;
    }
    used.add(id);
    const prev = storedItems.get(id);
    const keepFile = kind === 'acknowledge' && prev?.kind === 'acknowledge' && prev.policyFile && it.removePolicyFile !== true ? prev.policyFile : null;
    const item: TemplateItem = { id, kind, label, details };
    const dueRaw = typeof it.dueDays === 'string' && it.dueDays.trim() ? Number(it.dueDays) : it.dueDays;
    if (dueRaw != null && dueRaw !== '') {
      const due = Number(dueRaw);
      if (!Number.isInteger(due) || due < 0 || due > MAX_DUE_DAYS) return { value: null, error: `Item ${n + 1}: the due date is a number of days from 0 to ${MAX_DUE_DAYS}.` };
      item.dueDays = due;
    }
    if (kind === 'acknowledge') {
      item.url = url;
      item.policyFile = keepFile || null;
    }
    if (kind === 'upload') item.doc = doc;
    items.push(item);
  }
  if (uploads > MAX_REQUEST_ITEMS) return { value: null, error: `Up to ${MAX_REQUEST_ITEMS} upload items per checklist.` };
  if (orientation > 1) return { value: null, error: 'Add "Attend orientation" once.' };
  return { value: { id: clean(r.id, 128), name, items, roleMatch }, error: null };
}

/**
 * The template for a new hire: one whose roleMatch appears in their position (any case), else one for
 * everyone (blank roleMatch); alphabetical by name when several fit. Null when none fits.
 */
export function pickTemplateForHire<T extends { name: string; roleMatch?: string | null; items: unknown[] }>(templates: T[], position: unknown): T | null {
  const pos = typeof position === 'string' ? position.toLowerCase() : '';
  const usable = templates.filter((t) => Array.isArray(t.items) && t.items.length > 0).sort((a, b) => a.name.localeCompare(b.name));
  const byRole = usable.find((t) => (t.roleMatch || '').trim() && pos.includes((t.roleMatch || '').trim().toLowerCase()));
  if (byRole) return byRole;
  return usable.find((t) => !(t.roleMatch || '').trim()) ?? null;
}

/** When an item is due: dueDays after the checklist starts, or null. */
export function dueAtFor(item: { dueDays?: number | null }, startMs: number): number | null {
  return typeof item.dueDays === 'number' && Number.isFinite(item.dueDays) && item.dueDays >= 0 ? startMs + item.dueDays * MS_PER_DAY : null;
}

/* ------------------------------------------------------------------ the older checklists */

type LegacyCredentialingTemplate = {
  name?: string;
  roleMatch?: string;
  items?: Array<{ label?: string; required?: boolean; instructionalNotes?: string; resourceLinks?: Array<{ label?: string; url?: string }>; externalPortalLabel?: string; externalPortalUrl?: string }>;
};
type LegacyOnboardingTemplate = { steps?: Array<{ name?: string; dueDaysOffset?: number | null; trackId?: string | null }>; tracks?: Array<{ id?: string; name?: string }> };

/**
 * The older checklists as new-hire checklist templates (what "Add to my new hire checklist" saves):
 *   - each credentialing template: its documents become "Upload a document" items; the notes, links and
 *     outside-portal link go into the item's details for the hire;
 *   - onboarding steps: one template for the steps every hire gets, and one per track (used for
 *     positions containing the track's name); each step is a task you mark done, with its due days.
 * Labels that the no tax / I-9 / SSN / bank / e-signature rule blocks are left out.
 */
export function legacyChecklistsToTemplates(input: { credentialing?: LegacyCredentialingTemplate[]; onboarding?: LegacyOnboardingTemplate | null }): Array<{ source: 'credentialing' | 'onboarding'; name: string; roleMatch: string; items: Array<Omit<TemplateItem, 'id'>> }> {
  const out: Array<{ source: 'credentialing' | 'onboarding'; name: string; roleMatch: string; items: Array<Omit<TemplateItem, 'id'>> }> = [];
  for (const t of input.credentialing || []) {
    const items = (t.items || [])
      .filter((i) => clean(i.label, MAX_LABEL).length >= 2 && !blockedLabelReason(i.label))
      .slice(0, MAX_REQUEST_ITEMS)
      .map((i) => {
        const parts = [
          i.required === false ? 'Optional.' : '',
          clean(i.instructionalNotes, 300),
          ...(i.resourceLinks || []).filter((l) => isHttpsUrl(l?.url)).map((l) => `${clean(l.label, 60) || 'Link'}: ${String(l.url).trim()}`),
          isHttpsUrl(i.externalPortalUrl) ? `${clean(i.externalPortalLabel, 60) || 'Portal'}: ${String(i.externalPortalUrl).trim()}` : '',
        ].filter(Boolean);
        const details = parts.join(' ').slice(0, MAX_DETAILS);
        return { kind: 'upload' as const, label: clean(i.label, MAX_LABEL), details: blockedLabelReason(details) ? '' : details, doc: { kind: 'custom' as const } };
      });
    if (items.length) out.push({ source: 'credentialing', name: clean(t.name, MAX_TEMPLATE_NAME) || 'Documents to bring', roleMatch: clean(t.roleMatch, MAX_ROLE_MATCH), items });
  }
  const o = input.onboarding;
  if (o && Array.isArray(o.steps) && o.steps.length) {
    const tracks = new Map((o.tracks || []).filter((t) => t?.id && t?.name).map((t) => [String(t.id), clean(t.name, MAX_ROLE_MATCH)]));
    const groups = new Map<string, Array<Omit<TemplateItem, 'id'>>>();
    for (const st of o.steps) {
      const label = clean(st?.name, MAX_LABEL);
      if (label.length < 2 || blockedLabelReason(label)) continue;
      const key = st?.trackId && tracks.has(String(st.trackId)) ? String(st.trackId) : '';
      const due = typeof st?.dueDaysOffset === 'number' && st.dueDaysOffset >= 0 ? Math.min(MAX_DUE_DAYS, Math.round(st.dueDaysOffset)) : null;
      (groups.get(key) || groups.set(key, []).get(key)!).push({ kind: 'task', label, details: '', ...(due != null ? { dueDays: due } : {}) });
    }
    for (const [key, items] of groups) {
      out.push({ source: 'onboarding', name: key ? `Onboarding steps: ${tracks.get(key)}` : 'Onboarding steps', roleMatch: key ? tracks.get(key) || '' : '', items: items.slice(0, MAX_TEMPLATE_ITEMS) });
    }
  }
  return out;
}

/** Upload items as Request documents items (normalizeRequestItems input), in checklist order. */
export function uploadRequestInputs(items: TemplateItem[]): Array<{ itemId: string; input: { kind: 'catalog'; key: string } | { kind: 'custom'; label: string } }> {
  return items
    .filter((i) => i.kind === 'upload')
    .map((i) => ({ itemId: i.id, input: i.doc?.kind === 'catalog' ? { kind: 'catalog' as const, key: i.doc.key } : { kind: 'custom' as const, label: i.label } }));
}

/* ------------------------------------------------------------------ a hire's checklist */

export type StoredChecklistItem = {
  id: string;
  kind: ChecklistItemKind;
  label: string;
  details: string;
  url?: string | null;
  policyFile?: PolicyFile | null;
  /** upload: the matching item id on the checklist's document request (null when there is none). */
  requestItemId?: string | null;
  /** task / upload without a request: marked done by the employer. */
  done?: boolean;
  doneAtMs?: number | null;
  doneBy?: string | null;
  /** acknowledge: when the hire tapped "I have read this". */
  acknowledgedAtMs?: number | null;
  /** When it is due (the template's dueDays after the checklist started), or null. */
  dueAtMs?: number | null;
};

export type ItemState = 'done' | 'open' | 'waiting';

export type ComputedChecklistItem = StoredChecklistItem & {
  doneNow: boolean;
  state: ItemState;
  stateLabel: string;
  /** What the hire can do about it on their page. */
  hireAction: 'acknowledge' | 'upload' | 'book_orientation' | null;
  hasPolicyFile: boolean;
};

/** The live facts an item's status comes from. */
export type ChecklistContext = {
  /** Request documents item states by request item id (null = no request, or it was cancelled). */
  requestItems?: Record<string, { complete: boolean; status: string }> | null;
  orientation?: { attended: boolean; booked: boolean } | null;
};

const REQUEST_STATUS_LABEL: Record<string, string> = {
  requested: 'To upload',
  uploaded: 'Uploaded',
  reviewed: 'Documents Reviewed',
  overdue: 'Overdue',
};

export function computeChecklistItems(items: StoredChecklistItem[], ctx: ChecklistContext = {}): ComputedChecklistItem[] {
  return (Array.isArray(items) ? items : []).map((it) => {
    const base = { ...it, hasPolicyFile: !!it.policyFile };
    if (it.kind === 'acknowledge') {
      const done = typeof it.acknowledgedAtMs === 'number' && it.acknowledgedAtMs > 0;
      return { ...base, doneNow: done, state: done ? 'done' : 'open', stateLabel: done ? 'Acknowledged' : 'To read and acknowledge', hireAction: done ? null : 'acknowledge' };
    }
    if (it.kind === 'upload') {
      const req = it.requestItemId && ctx.requestItems ? ctx.requestItems[it.requestItemId] : undefined;
      if (req) {
        const done = req.complete;
        return { ...base, doneNow: done, state: done ? 'done' : req.status === 'uploaded' ? 'waiting' : 'open', stateLabel: REQUEST_STATUS_LABEL[req.status] || 'To upload', hireAction: done || req.status === 'uploaded' ? null : 'upload' };
      }
      const done = it.done === true;
      return { ...base, doneNow: done, state: done ? 'done' : 'waiting', stateLabel: done ? 'Done' : 'Your employer marks this done', hireAction: null };
    }
    if (it.kind === 'orientation') {
      const attended = !!ctx.orientation?.attended;
      const booked = !!ctx.orientation?.booked;
      return { ...base, doneNow: attended, state: attended ? 'done' : booked ? 'waiting' : 'open', stateLabel: attended ? 'Attended' : booked ? 'Booked' : 'Not booked yet', hireAction: attended || booked ? null : 'book_orientation' };
    }
    const done = it.done === true;
    return { ...base, doneNow: done, state: done ? 'done' : 'waiting', stateLabel: done ? 'Done' : 'Your employer marks this done', hireAction: null };
  });
}

/** "Due Oct 30" or "Overdue since Oct 30" for an open item with a due date; null otherwise. */
export function dueLabel(item: { dueAtMs?: number | null; doneNow: boolean }, nowMs: number = Date.now()): { text: string; overdue: boolean } | null {
  if (item.doneNow || typeof item.dueAtMs !== 'number' || !Number.isFinite(item.dueAtMs)) return null;
  const day = new Date(item.dueAtMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return item.dueAtMs < nowMs ? { text: `Overdue since ${day}`, overdue: true } : { text: `Due ${day}`, overdue: false };
}

export function checklistProgress(items: Array<{ doneNow: boolean }>): { done: number; total: number; complete: boolean } {
  const total = items.length;
  const done = items.filter((i) => i.doneNow).length;
  return { done, total, complete: total > 0 && done === total };
}

/** "7/10" plus words, so progress is never colour alone. */
export function progressLabel(p: { done: number; total: number }): string {
  if (!p.total) return 'No items';
  if (p.done === p.total) return `${p.done}/${p.total} complete`;
  return `${p.done}/${p.total} done`;
}

/** Items the hire still has to act on themselves (the only ones a reminder is about). */
export function openHireItems(items: ComputedChecklistItem[]): ComputedChecklistItem[] {
  return items.filter((i) => !i.doneNow && i.hireAction !== null);
}

/* ------------------------------------------------------------------ reminders */

export type ChecklistReminderState = { lastSentDay?: string | null; lastAtMs?: number | null; count?: number | null };

/**
 * Whether the hourly job should email a reminder now, in the hire's zone `tz`:
 *  - only while the checklist is active and the hire has something to do;
 *  - at most ONE email per hire per local day (counting a Request documents email sent that day for
 *    the same hire, `otherEmailDay`);
 *  - never in quiet hours (1-6 AM local; the next hourly run tries again);
 *  - the first reminder two days after the checklist was applied, then at most every three days,
 *    MAX_REMINDERS in all.
 */
export function hireChecklistReminderDue(input: {
  active: boolean;
  openForHire: number;
  createdAtMs: number;
  reminders?: ChecklistReminderState | null;
  otherEmailDay?: string | null;
  nowMs: number;
  tz: string;
}): { due: boolean; reason: string } {
  const r = input.reminders || {};
  if (!input.active) return { due: false, reason: 'not_active' };
  if (input.openForHire <= 0) return { due: false, reason: 'nothing_open' };
  const today = zonedDateKey(input.nowMs, input.tz);
  if (r.lastSentDay === today || input.otherEmailDay === today) return { due: false, reason: 'already_today' };
  if ((r.count || 0) >= MAX_REMINDERS) return { due: false, reason: 'max_reached' };
  if (!mayAutoSendNow(input.tz, 'email', input.nowMs)) return { due: false, reason: 'quiet_hours' };
  if (input.nowMs < input.createdAtMs + FIRST_REMINDER_AFTER_MS) return { due: false, reason: 'too_soon' };
  if (typeof r.lastAtMs === 'number' && input.nowMs < r.lastAtMs + REMINDER_GAP_MS) return { due: false, reason: 'gap' };
  return { due: true, reason: (r.count || 0) === 0 ? 'first_reminder' : 'next_reminder' };
}

/** Subject and plain-text lines of the hire's email (the server adds HTML escaping). No em dashes. */
export function hireChecklistEmailContent(input: {
  kind: 'initial' | 'reminder';
  orgName: string;
  firstName?: string | null;
  openLabels: string[];
}): { subject: string; intro: string; items: string[]; ctaLabel: string } {
  const org = input.orgName || 'Your new employer';
  const hi = input.firstName ? `Hi ${input.firstName},` : 'Hi,';
  const items = input.openLabels.slice(0, 12);
  if (input.kind === 'initial') {
    return {
      subject: `${org}: your new hire checklist`,
      intro: `${hi} ${org} added a new hire checklist for you on Arevina. Open it to see what to read, upload and attend before you start.`,
      items,
      ctaLabel: 'Open my checklist',
    };
  }
  return {
    // The email lists at most 12 items; the subject counts every open item.
    subject: `Reminder: ${input.openLabels.length} item${input.openLabels.length === 1 ? '' : 's'} left on your ${org} checklist`,
    intro: `${hi} a reminder that these items on your ${org} new hire checklist are still open.`,
    items,
    ctaLabel: 'Open my checklist',
  };
}

/** Deterministic checklist id: one checklist per hire per organization. */
export function hireChecklistId(orgId: string, emtUid: string): string {
  return `${orgId}__${emtUid}`;
}
